import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DriverProfile, CarrierOwnerProfile, DispatcherProfileComponent } from '../components/profiles';
import { toast } from 'sonner';

const LearnTheTradeProfile = lazy(() => import('../components/profiles/LearnTheTradeProfile'));
const AffiliateProfile = lazy(() => import('../components/profiles/AffiliateProfile'));
const ServiceProviderProfile = lazy(() => import('../components/profiles/ServiceProviderProfile'));

const Profile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile/me');
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Render role-specific profile component
  const renderProfileByRole = () => {
    const role = user?.role || 'driver';
    
    switch (role) {
      case 'driver':
        return <DriverProfile profile={profile} user={user} onUpdate={fetchProfile} />;
      case 'carrier_owner':
        return <CarrierOwnerProfile profile={profile} user={user} onUpdate={fetchProfile} />;
      case 'dispatcher':
        return <DispatcherProfileComponent profile={profile} user={user} onUpdate={fetchProfile} />;
      case 'learner':
        return <Suspense fallback={<div className="text-center py-12 text-slate-400">Loading...</div>}><LearnTheTradeProfile profile={profile} user={user} onUpdate={fetchProfile} /></Suspense>;
      case 'affiliate':
        return <Suspense fallback={<div className="text-center py-12 text-slate-400">Loading...</div>}><AffiliateProfile profile={profile} user={user} onUpdate={fetchProfile} /></Suspense>;
      case 'service_provider':
        return <Suspense fallback={<div className="text-center py-12 text-slate-400">Loading...</div>}><ServiceProviderProfile profile={profile} user={user} onUpdate={fetchProfile} /></Suspense>;
      case 'admin':
        return <CarrierOwnerProfile profile={profile} user={user} onUpdate={fetchProfile} />;
      default:
        return <DriverProfile profile={profile} user={user} onUpdate={fetchProfile} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full" data-testid="profile-page">
        {renderProfileByRole()}
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
